/**
 * useDataLists — replaces AngularJS DataListService.
 *
 * Original always fetched from API (the `if (true || !datalists)` guard was
 * intentionally short-circuited).  We preserve that behaviour here.
 */

import { useCallback, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '@/env';
import type { DataListItem, DataLists } from '@/types';

interface RawDataList {
  name: string;
  data: string; // JSON-encoded array of DataListItem
}

function expand(raw: RawDataList[]): DataLists {
  const out: DataLists = {};
  for (const item of raw) {
    try {
      const arr = JSON.parse(item.data) as DataListItem[];
      out[item.name] = arr.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
    } catch {
      out[item.name] = [];
    }
  }
  return out;
}

interface UseDataListsReturn {
  datalists: DataLists;
  loading: boolean;
  fetchDataLists: () => Promise<void>;
}

export function useDataLists(): UseDataListsReturn {
  const [datalists, setDataLists] = useState<DataLists>({});
  const [loading, setLoading] = useState(false);

  const fetchDataLists = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await axios.get<{ success: boolean; data: RawDataList[] }>(
        `${BASE_URL}datalists`,
      );
      if (data.success) {
        window.localStorage.setItem('datalists', JSON.stringify(data.data));
        setDataLists(expand(data.data));
      }
    } catch {
      // Fall back to localStorage if offline
      const cached = window.localStorage.getItem('datalists');
      if (cached) setDataLists(expand(JSON.parse(cached) as RawDataList[]));
    } finally {
      setLoading(false);
    }
  }, []);

  return { datalists, loading, fetchDataLists };
}
